﻿import React, { useEffect, useRef, useState } from "react";
import styles from "./AutonomousAP.module.css";
import { getResponsiveValue } from "../../util_glob";
import debounce from "lodash/debounce";
import SlideNav from "./SlideNav";
import { ScrollDownAnimatedArrows } from "../ScrollDownAnimatedArrows/ScrollDownAnimatedArrows";

const AutonomousCallCenter: React.FC = () => {
    const containerRef = useRef<HTMLElement | null>(null);
    const lenisRef = useRef<any>(null);
    const rafId = useRef<number | null>(null);
    const currentActive = useRef<HTMLElement | null>(null);
    const [activeSlide, setActiveSlide] = useState(-1);
    const [isHiddenSlideOne, setIsHiddenSlideOne] = useState(false);
    const [hitTop, setHitTop] = useState<boolean>();

    const marginpx  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 200,
        maxHeight: 2000,
        minValue: 40,
        maxValue: 100,
    })); // extra margin for intersection observer
    const interactionthreshold1  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.15,
        maxValue: 0.2
    }))
    const interactionthreshold2  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.4,
        maxValue: 0.5
    }))
    const interactionthreshold3  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.6,
        maxValue: 0.9
    }))
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;

        const box = document.querySelector('.scrollable');
        box?.classList.add('hide-scrollbar');

        let mounted = true;
        let lenisScrollHandler: any = null;
        let snapTimeout: number | null = null;

        (async () => {
            try {
                const mod = await import("lenis");
                const Lenis = (mod && (mod.default || mod)) as any;
                if (!mounted) return;
                lenisRef.current = new Lenis({
                    smooth: true,
                    lerp: 0.1,
                    duration:0.7,
                    smoothWheel: true,
                    smoothTouch: true,
                    syncTouch: true,
                    autoRaf: true,
                    touchInertiaExponent: 12
                });
            } catch (err) {
                console.warn("Lenis failed to load, falling back to native scroll.", err);
            }
        })();

        const slidesForObserver = root.querySelectorAll("." + styles.slide);

        const findNearestToCenter = () => {
            const activeEntries: NodeListOf<HTMLElement> = root.querySelectorAll("." + styles.active);
            let el = activeEntries.length === 1 ? activeEntries[0] : null;
            if (el) return el;

        };

        const centerSlide = (el: HTMLElement) => {
            if (!el) return;
            try {
                // duration in ms-ish — adjust for snappiness
                lenisRef.current?.scrollTo(el, {
                    offset: -window.innerHeight / 2 + el.offsetHeight / 2, // centers the element vertically
                    duration: 0.5,
                    lock: true,
                    onComplete: () => { lenisRef }
                })
            } catch (e) {
                import.meta.env.DEV && console.log("‹errr_cntr_sld›", e);
            }
        };

        // scroll event handler: debounce and snap when user stops
        const onLenisScroll = debounce((newActive: HTMLElement) => {
            const nearest = findNearestToCenter();
            centerSlide(newActive);
        }, 1);

        const onScroll = () => {
            if (window.scrollY < 40){
                if (hitTop != true) setHitTop(true)
                if (activeSlide != 0) setActiveSlide(0);
                if (isHiddenSlideOne != true) setIsHiddenSlideOne(true);
                currentActive.current = null;
            } else {
                if (hitTop != false) setHitTop(false)
                if (isHiddenSlideOne == true) setIsHiddenSlideOne(false);
            }
        }


        const handleResize = () => {
            marginpx.current = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 800, maxHeight: 3000,
                minValue: 0, maxValue: 100
            })
            interactionthreshold1.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400, maxHeight: 3000,
                minValue: 0.3, maxValue: 0.4
            })
            interactionthreshold2.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400, maxHeight: 3000,
                minValue: 0.7, maxValue: 0.1
            })
            interactionthreshold3.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400, maxHeight: 3000,
                minValue: 0.6, maxValue: 0.9
            })
        };
        {/* scroll listener to detect hitting top */}
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", handleResize);

        const observer = new IntersectionObserver((entries) => {
            // find intersecting entries
            const visibleEntries = entries.filter((e) => e.isIntersecting && e.intersectionRatio >= getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400, maxHeight: 3000,
                minValue: 0.2, maxValue: 0.5,
            }));

            if (visibleEntries.length > 0) {
                // pick the most visible one
                const mostVisible = visibleEntries.reduce((prev, curr) =>
                    prev.intersectionRatio > curr.intersectionRatio ? prev : curr
                );
                const newActive = mostVisible.target as HTMLElement;
                setIsHiddenSlideOne(false);
                if (newActive !== currentActive.current) { // only run logic if a different slide is now active
                    // remove .active from all slides
                    setActiveSlide(newActive?.dataset.bg === "cc1" ? 1 :
                        newActive?.dataset.bg === "cc2" ? 2 :
                        newActive?.dataset.bg === "cc3" ? 3 :
                        newActive?.dataset.bg === "cc4" ? 4 :
                        newActive?.dataset.bg === "cc5" ? 5 : 0);

                    slidesForObserver.forEach((el) => el.classList.remove(styles.active));

                    // add .active to the new one
                    newActive.classList.add(styles.active);

                    // update current active
                    currentActive.current = newActive;

                    // ✅ call your debounced Lenis scroll handler here
                    onLenisScroll(newActive);
                }
            }
        }, { threshold: [ interactionthreshold3.current ], root: null, rootMargin: `${marginpx.current}px` });


        slidesForObserver.forEach((s) => observer.observe(s));

        return () => {
            mounted   = false;
            observer.disconnect();
            const box = document.querySelector('.scrollable');
            box?.classList.add('hide-scrollbar !important'); // hide scrollbar

            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", onScroll);
            if (rafId.current) cancelAnimationFrame(rafId.current);
            if (lenisRef.current) {
                try {
                    if (typeof lenisRef.current.off === "function" && lenisScrollHandler) lenisRef.current.off("scroll", lenisScrollHandler);
                    if (typeof lenisRef.current.destroy === "function") lenisRef.current.destroy();
                } catch (e) { /* ignore */ }
                lenisRef.current = null;
            }
            if (snapTimeout) window.clearTimeout(snapTimeout);
        };
    }, []);

    const navHome = () => {
        const element = document.getElementById("kgnfLoginCont") as HTMLElement;
        setActiveSlide(0);
        if (lenisRef.current && element) {
            lenisRef.current.scrollTo(element, {
                offset: -window.innerHeight / 2 + element.clientHeight / 2,
                duration: 0.5,
                lock: true,
                easing: "easeInOut"
            });
        }
    };
    return (
        <section ref={containerRef} className={styles.wrapper} aria-label="Autonomous Call Center">
            {/* slide 1: title + one-sentence lead (storybook first slide) */}
            <div className={styles.slide} data-bg="cc1">
                <div className={styles.centerContentACC}>
                    <h2 className={styles.title}>Autonomous voice operations ready for the real world</h2>
                </div>
                <h3 className={styles.subtitle}>Kognify Call Center</h3>
                <ScrollDownAnimatedArrows contClassName="aquaArrows" clickAreaClassName="aquaArrowsClickArea"/>
            </div>

            {/* slide 2: Big question left, Modern Systems + three huge elements right */}
            <div className={[styles.slide, styles.semiSplit].join(" ")} data-bg="cc2">
                <div className={styles.halfLeft}>
                    <blockquote className={styles.bigQuestion}>
                        You searched for AI solutions at the required quiality. Did you find one?
                        <div className={styles.sectionSubSub} />
                    </blockquote>
                </div>

                <div className={styles.halfRight}>
                    <div className={styles.sectionSubSubSub}>
                        Most "Voice AI" fail to meet real world demands leading to poor caller experience, high agent intervention, and no real improvement in operations.
                    </div>
                    <div className={styles.bigGrid}>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0s" }}>
                            <div className={styles.cardHeading}>Non-English Capabilities</div>
                            <div className={styles.cardBody}>
                                Non-English voice experiences demand dedicated language-native approach.
                                Issues like unreliable data recognition, bad language cadence & pronunciation compound to poor experience.
                            </div>
                        </div>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.cardHeading}>Cadance and Latency</div>
                            <div className={styles.cardBody}>
                                Most systems respond too slowly (over 1.5 sec) and in robotic fashion.
                                This kills conversation flow, frustrates callers, and makes the system seem incompetent & archaic.
                            </div>
                        </div>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0.24s" }}>
                            <div className={styles.cardHeading}>Scenario Control & Data Handling</div>
                            <div className={styles.cardBody}>
                                Real world scenarios require secure user-specific data collection/delivery, robust scenario control and unpredictable conditions handling.
                                All essential to trust outcomes, compliance, or reporting.
                            </div>
                        </div>
                    </div>
                </div>
                <ScrollDownAnimatedArrows contClassName="whiteArrows"/>
            </div>

            {/* slide 3: huge impactful quote + "unlike current systems" and steps + abilities showcase */}
            <div className={[styles.slide, styles.combined].join(" ")} data-bg="cc3">
                <div className={styles.combinedLeft}>
                    <blockquote className={styles.hugeImpactCombined}>
                        Customers want resolution and clarity - not gimmics.
                    </blockquote>
                </div>

                <div className={styles.combinedRight}>
                    <div className={styles.sectionSubSubSub}>Kognify Voice is fast, controllable, and built for real call center stacks</div>

                    <div className={styles.stepsWrap}>
                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.cardHeading}>Bulgarian-grade speech</div>
                            <div className={styles.cardBody}>
                                Language-specific handling for speaking and listening, tuned for real callers, real noise, and real phrasing.
                                The goal is consistent understanding, not “demo room” performance.
                            </div>
                        </div>

                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.cardHeading}>Human-cadence engine</div>
                            <div className={styles.cardBody}>
                                The assistant knows when to speak, when to wait, and how to acknowledge delays.
                                In normal conditions, it starts responding quickly enough to keep the call feeling continuous and natural.
                            </div>
                        </div>

                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.cardHeading}>Scenario execution + data capture</div>
                            <div className={styles.cardBody}>
                                Define scenario steps & conditions, data for collection (training, interview results, etc.) and general knowledge.
                                Add user identifiers (e.g., phone, EGN/EIK, vehicle number, etc.),.
                            </div>
                        </div>

                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.cardHeading}>Secure data delivery and integrations</div>
                            <div className={styles.cardBody}>
                                Deliver personalised data from your systems (CRM, ticketing, billing) or initiate actions
                                - all without forcing migrations or replacing your telephony.
                            </div>
                        </div>
                    </div>
                </div>
                <ScrollDownAnimatedArrows contClassName="darkBlueArrows"/>
            </div>

            {/* slide 4: We deliver (very large showcase of four points) */}
            <div className={[styles.slide, styles.deliverSlide].join(" ")} data-bg="cc4">
                <div className={styles.deliverGrid}>
                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Always On</div>
                        <div className={styles.deliverText}>
                            Answer calls instantly, reduce unanswered calls and abandonment, and protect your brand during peak load.
                        </div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Lignting quick responses</div>
                        <div className={styles.deliverText}>
                            Consistently hits sub 1.5s latency in real world calls. Designed to avoid “dead air” and keep conversation flow natural with immediate acknowledgements and tight turn-taking.
                        </div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>On-script</div>
                        <div className={styles.deliverText}>
                            Scenario-driven handling with reliable field collection, validation, and auditable outcomes — plus optional operator escalation.
                        </div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>No rip-and-replace</div>
                        <div className={styles.deliverText}>
                            Integrate into existing call center and business systems with minimal friction. Add AI value without risky migrations.
                        </div>
                    </div>
                </div>
                <ScrollDownAnimatedArrows contClassName="greenArrows"/>
            </div>

            {/* slide 5: Try */}
            <div className={[styles.slide, styles.deliverSlideAAP].join(" ")} data-bg="cc5">
                <div className={styles.tryGridAAP}>
                    <div className={styles.demoDescr}><div>De-escalation Training </div><div>in Bulgarian Language</div></div>

                    {/* Replace this link with your call-center demo route / redeem code */}
                    <div
                        className={styles.tryCard}
                        onClick={() => {
                            window.location.href = `${window.location.origin}?redeemCode=%D0%9C%D0%9C3_bcd6b981-200b-4496-842f-9ec22955bf08_215ox&spt=De-escalation%20Training&story=acc&A=1`;
                        }}
                    >
                        <div className={styles.deliverNumber}>Start Voice Demo</div>
                        <div className={styles.tryText}>Try <strong>instantly</strong> without registration</div>
                    </div>
                </div>
            </div>

            <div className={styles.decor + (isHiddenSlideOne == true ? ` ${styles.decorHidden}` : "")} aria-hidden="true" />
            {hitTop != true && (
                <SlideNav
                    activeSlide={activeSlide}
                    activeStoryId="acc"
                    navHome={() => {navHome()}}
                    callSlideid={(element, slideIndex) => {
                        setActiveSlide(slideIndex);

                        if (lenisRef.current) {
                            lenisRef.current.scrollTo(element, {
                                offset: -window.innerHeight / 2 + element.clientHeight / 2,
                                duration: 0.5,
                                lock: true,
                                easing: "easeInOut",
                            });
                        }
                    }}
                />
            )}
        </section>
    );
};

export default AutonomousCallCenter;
